import {useDialog} from 'platform/components';
import {Box, Hide, Icon, Show, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {useLocation} from 'react-router-dom';

import {AI_ASSISTANT_LAUNCHER_SLOT_SIZE, aiAssistantPresentationStore} from '@dms/ai-assistant';
import {MenuItemProps, UserSidebarIconSize} from '@dms/api/shared';
import {moduleKeys} from '@dms/config';
import {featureFlags} from '@dms/feature-flags';
import {useNavigationBreadCrumb} from '@dms/shared';

import {getSafely, pushGtmEvent, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SIDEBAR_DESKTOP_ICON_SIZE} from '../constants/sidebarDesktopIconSize';
import {useCommunicationMenuBadgeValue} from '../hooks/useCommunicationMenuBadgeValue';
import {useSidebarMenuItemsOverflow} from '../hooks/useSidebarMenuItemsOverflow';
import {NavigationButton} from './NavigationButton';
import {SidebarCreateEntityShortcut} from './SidebarCreateEntityShortcut';
import {SidebarEditDialog} from './SidebarEditDialog';
import {useSidebarKeyboardControl} from './SidebarKeyboardControlProvider';
import {SidebarLink} from './SidebarLink';
import {SidebarOverflowMenu} from './SidebarOverflowMenu';

// Callback ref: hands the recess element to the assistant (null on unmount) so the launcher can
// portal into it even when the sidebar mounts after the assistant. Aliased at module level because
// the react-compiler lint reads `store.setLauncherSlot` passed to `ref` as a ref access.
const registerLauncherSlot = aiAssistantPresentationStore.setLauncherSlot;

interface SidebarNavigationProps extends RequiredTestIdProps {
  createMenuItems: MenuItemProps[];
  menuItemsById: Record<string, MenuItemProps>;
  pinnedMenuItems: string[];
  iconSize: UserSidebarIconSize;
}

export function SidebarNavigation(props: SidebarNavigationProps) {
  const location = useLocation();
  const [breadcrumbProps] = useNavigationBreadCrumb();

  const [isEditDialogOpen, onEditDialogOpen, onEditDialogClose] = useDialog(false);

  const {
    containerRef,
    additionalContentContainerRef,
    visiblePinnedMenuItems,
    overflownPinnedMenuItems,
  } = useSidebarMenuItemsOverflow({
    pinnedMenuItems: props.pinnedMenuItems,
  });

  const {setSidebarItemRef: setItemRef} = useSidebarKeyboardControl();
  const communicationBadgeValue = useCommunicationMenuBadgeValue();

  return (
    <Box ref={containerRef} backgroundColor="palettes.neutral.900.100" width={12} flexGrow={1}>
      <VStack height="100%" align="center" justify="space-between" spacing={1}>
        <Box width="100%">
          {visiblePinnedMenuItems.map((id) => {
            const menuItem = getSafely(props.menuItemsById, id);
            if (!menuItem) {
              return null;
            }

            const isActive = Boolean(
              location.pathname === menuItem.path || breadcrumbProps?.navigateTo === menuItem.path
            );

            return (
              <SidebarLink
                key={menuItem.id}
                ref={(element) => setItemRef(element, id)}
                iconSize={props.iconSize}
                path={menuItem.path}
                icon={menuItem.layoutProps.icon}
                title={menuItem.layoutProps.title}
                action={menuItem.action}
                isActive={isActive}
                badgeValue={
                  menuItem.id === moduleKeys.communication ? communicationBadgeValue : undefined
                }
                data-testid={suffixTestId(`sidebar-link-${menuItem.id}`, props)}
              />
            );
          })}

          <Show when={overflownPinnedMenuItems.length > 0}>
            <SidebarOverflowMenu
              iconSize={props.iconSize}
              pinnedMenuItems={overflownPinnedMenuItems}
              menuItemsById={props.menuItemsById}
            />
          </Show>
        </Box>

        <Box width="100%" ref={additionalContentContainerRef}>
          <Box width="100%">
            <Hide
              when={props.createMenuItems.length === 0}
              whenFeatureEnabled={featureFlags.SIDEBAR_REMOVE_QUICK_ACTIONS}
            >
              <SidebarCreateEntityShortcut
                ref={(element) => setItemRef(element, 'sidebar-create')}
                iconSize={props.iconSize}
                menuItems={props.createMenuItems}
                menuItemsById={props.menuItemsById}
                data-testid={props['data-testid']}
              />
            </Hide>
            <NavigationButton
              ref={(element) => setItemRef(element, 'sidebar-edit')}
              onClick={() => {
                onEditDialogOpen();
                pushGtmEvent({
                  event: 'button_click',
                  event_id: 'sidebar_settings_open',
                  value: {
                    pinnedItems: props.pinnedMenuItems,
                    iconSize: props.iconSize,
                  },
                });
              }}
              isActive={isEditDialogOpen}
            >
              <Icon
                color="text.white"
                size={SIDEBAR_DESKTOP_ICON_SIZE[props.iconSize]}
                value="sidebar/edit"
              />
            </NavigationButton>
            <Show when={isEditDialogOpen}>
              <SidebarEditDialog
                onClose={onEditDialogClose}
                iconSize={props.iconSize}
                menuItemsById={props.menuItemsById}
                pinnedMenuItems={props.pinnedMenuItems}
                data-testid={props['data-testid']}
              />
            </Show>
            <Show whenFeatureEnabled={featureFlags.OMNETIC_AI_ASSISTANT}>
              <LauncherSlotArea>
                <LauncherSlot ref={registerLauncherSlot} />
              </LauncherSlotArea>
            </Show>
          </Box>
        </Box>
      </VStack>
    </Box>
  );
}

const LauncherSlotArea = styled.div`
  display: flex;
  width: 100%;
  padding: ${({theme}) => `${theme.getSize(2)} 0 ${theme.getSize(3)}`};
  justify-content: center;
`;

// The recess reads as a place the launcher belongs to — it stays visible while the launcher is
// pulled out of it, so it is drawn here rather than by the launcher itself.
const LauncherSlot = styled.div`
  position: relative;
  width: ${({theme}) => theme.getSize(AI_ASSISTANT_LAUNCHER_SLOT_SIZE)};
  height: ${({theme}) => theme.getSize(AI_ASSISTANT_LAUNCHER_SLOT_SIZE)};
  flex: none;
  border-radius: ${({theme}) => theme.radii.circular};
  background: rgba(0, 0, 0, 0.35);
  box-shadow:
    inset 0 ${({theme}) => theme.getSize(1)} ${({theme}) => theme.getSize(2)} rgba(0, 0, 0, 0.6),
    0 1px 0 rgba(255, 255, 255, 0.06);
`;
